import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Upload, Button, Breadcrumb, Grid, Card, Row, Col,
  Statistic, Avatar, Tag, Tooltip, Dropdown, Select, Switch, DatePicker, Space, Image,
  ColorPicker, Divider
} from 'antd';
import { bannerService } from '../../common/services';
import {
  EditOutlined,
  DeleteOutlined,
  RightOutlined,
  SearchOutlined,
  PictureOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CalendarOutlined,
  LinkOutlined,
  OrderedListOutlined,
  NotificationOutlined,
  BgColorsOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

// Formater la date
const formatDate = (date) => {
  if (!date) return '-';
  const formats = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
  ];
  let parsed = dayjs(date);
  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }
  if (typeof date === 'string') return date;
  return '-';
};

const positionLabels = {
  hero: { label: 'Hero (Haut)', color: 'gold' },
  middle: { label: 'Milieu', color: 'blue' },
  bottom: { label: 'Bas', color: 'green' },
};

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    hero: 0,
    middle: 0,
    bottom: 0
  });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [currentPosition, setCurrentPosition] = useState('middle');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const active = data.filter(item => item.isActive).length;
    const hero = data.filter(item => item.position === 'hero').length;
    const middle = data.filter(item => item.position === 'middle').length;
    const bottom = data.filter(item => item.position === 'bottom').length;

    setStats({
      total: data.length,
      active,
      hero,
      middle,
      bottom
    });
  }, []);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await bannerService.getAll();
      const bannersData = response?.data || [];
      setBanners(bannersData);
      calculateStats(bannersData);
    } catch (err) {
      console.error('Erreur lors du chargement des bannières :', err);
      message.error('Erreur lors du chargement des bannières');
    } finally {
      setLoading(false);
    }
  };

  const filteredBanners = banners.filter((banner) => {
    const matchSearch = banner.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       banner.subtitle?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPosition = !filterPosition || banner.position === filterPosition;
    const matchStatus = filterStatus === '' ||
                       (filterStatus === 'active' && banner.isActive) ||
                       (filterStatus === 'inactive' && !banner.isActive);
    return matchSearch && matchPosition && matchStatus;
  });

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedBanner(null);
    form.resetFields();
    setFileList([]);
    setCurrentPosition('middle');
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setIsEditMode(true);
    setSelectedBanner(record);
    setCurrentPosition(record.position || 'middle');

    // Parser les dates avec le bon format (DD/MM/YYYY HH:mm venant du backend)
    let dateRange = null;
    if (record.startDate && record.endDate) {
      const startParsed = dayjs(record.startDate, 'DD/MM/YYYY HH:mm');
      const endParsed = dayjs(record.endDate, 'DD/MM/YYYY HH:mm');
      if (startParsed.isValid() && endParsed.isValid()) {
        dateRange = [startParsed, endParsed];
      }
    }

    form.setFieldsValue({
      title: record.title,
      subtitle: record.subtitle,
      linkUrl: record.linkUrl,
      position: record.position,
      order: record.order,
      isActive: record.isActive,
      dateRange,
      // Champs promo
      showPromoBox: record.showPromoBox || false,
      promoMessage: record.promoMessage || '',
      promoSubtext: record.promoSubtext || '',
      promoBackgroundColor: record.promoBackgroundColor || '#D4A853',
      promoTextColor: record.promoTextColor || '#ffffff',
    });
    setFileList([]);
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            style={{ backgroundColor: '#ff4d4f' }}
            icon={<DeleteOutlined />}
          />
          <span>Supprimer la bannière</span>
        </div>
      ),
      content: (
        <Card
          size="small"
          style={{ marginTop: 16, background: '#fff1f0', border: '1px solid #ffa39e' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={record.imageUrl}
              alt={record.title}
              style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 8 }}
            />
            <div>
              <p style={{ marginBottom: 0, fontWeight: 500 }}>{record.title}</p>
              <p style={{ marginBottom: 0, fontSize: 12, color: '#8c8c8c' }}>Cette action est irréversible</p>
            </div>
          </div>
        </Card>
      ),
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      centered: true,
      width: isMobile ? 360 : 480,
      okButtonProps: { danger: true, size: controlSize },
      cancelButtonProps: { size: controlSize },
      onOk: async () => {
        try {
          await bannerService.delete(record._id);
          message.success('Bannière supprimée avec succès');
          fetchBanners();
        } catch (error) {
          console.error('Erreur suppression :', error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleToggle = async (record) => {
    try {
      await bannerService.toggle(record._id);
      message.success(`Bannière ${record.isActive ? 'désactivée' : 'activée'} avec succès`);
      fetchBanners();
    } catch (error) {
      console.error('Erreur toggle :', error);
      message.error("Erreur lors de la modification du statut");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Gérer les dates (vérifier qu'elles sont valides avant de les convertir)
      let startDate = null;
      let endDate = null;
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        if (values.dateRange[0].isValid()) {
          startDate = values.dateRange[0].toISOString();
        }
        if (values.dateRange[1].isValid()) {
          endDate = values.dateRange[1].toISOString();
        }
      }

      // Extraire les couleurs (peut être un objet Color ou une string)
      const getColorValue = (color) => {
        if (!color) return null;
        if (typeof color === 'string') return color;
        if (typeof color?.toHexString === 'function') return color.toHexString();
        return null;
      };

      const data = {
        title: values.title,
        subtitle: values.subtitle || '',
        linkUrl: values.linkUrl || '',
        position: values.position,
        order: values.order || 0,
        isActive: values.isActive !== false,
        startDate,
        endDate,
        // Champs promo (uniquement pour position hero)
        showPromoBox: values.position === 'hero' ? (values.showPromoBox || false) : false,
        promoMessage: values.promoMessage || '',
        promoSubtext: values.promoSubtext || '',
        promoBackgroundColor: getColorValue(values.promoBackgroundColor) || '#D4A853',
        promoTextColor: getColorValue(values.promoTextColor) || '#ffffff',
      };

      // Gérer l'image
      if (fileList.length > 0 && fileList[0].originFileObj) {
        data.image = fileList[0].originFileObj;
      } else if (selectedBanner?.imageUrl) {
        data.imageUrl = selectedBanner.imageUrl;
      }

      if (isEditMode && selectedBanner) {
        await bannerService.update(selectedBanner._id, data);
        message.success("Bannière mise à jour avec succès");
      } else {
        if (!data.image && !data.imageUrl) {
          message.error("Une image est requise");
          return;
        }
        await bannerService.create(data);
        message.success("Bannière créée avec succès");
      }

      setModalOpen(false);
      fetchBanners();
    } catch (error) {
      console.error("Erreur de sauvegarde :", error);
      message.error("Erreur lors de la sauvegarde");
    }
  };

  // Export Excel
  const exportToExcel = () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredBanners.map(item => ({
        Titre: item.title,
        SousTitre: item.subtitle,
        Position: positionLabels[item.position]?.label || item.position,
        Ordre: item.order,
        Actif: item.isActive ? 'Oui' : 'Non',
        DateCreation: formatDate(item.createdAt),
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bannieres");
      XLSX.writeFile(workbook, "bannieres_website.xlsx");
      message.success(`${filteredBanners.length} bannières exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export Excel');
    } finally {
      hideLoading();
    }
  };

  // Export CSV
  const exportToCSV = () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredBanners.map(item => ({
        Titre: item.title,
        SousTitre: item.subtitle,
        Position: positionLabels[item.position]?.label || item.position,
        Ordre: item.order,
        Actif: item.isActive ? 'Oui' : 'Non',
        DateCreation: formatDate(item.createdAt),
      })));
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'bannieres_website.csv');
      message.success(`${filteredBanners.length} bannières exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      hideLoading();
    }
  };

  // Export PDF
  const exportToPDF = () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des bannières Website", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Titre', 'Position', 'Ordre', 'Actif', 'Date']],
        body: filteredBanners.map(item => [
          item.title,
          positionLabels[item.position]?.label || item.position,
          item.order,
          item.isActive ? 'Oui' : 'Non',
          formatDate(item.createdAt)
        ]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [250, 140, 22] },
      });

      doc.save("bannieres_website.pdf");
      message.success(`${filteredBanners.length} bannières exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export PDF');
    } finally {
      hideLoading();
    }
  };

  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#fa8c16' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  const columns = [
    {
      title: 'Bannière',
      key: 'banner',
      width: isMobile ? 200 : 350,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 100,
            height: 60,
            borderRadius: 8,
            overflow: 'hidden',
            border: '2px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Image
              src={record.imageUrl}
              alt={record.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9444feli6KbzUOzAAC1JM8V/VgAAAAAAAAAAAAAAAP///8ADhv3B+WLUAAAAXRSTlP/////////f/+fy/X1AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAvpJREFUeNrs2T1uwzAMBeDEKBqgQ4/gBRq26Bh0aA/QsWP/P0AzGBhFgOiYI/gII8nJoIKkH4pUXA=="
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#262626' }}>
              {record.title}
            </div>
            {record.subtitle && (
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                {record.subtitle}
              </div>
            )}
            <Tag color={positionLabels[record.position]?.color || 'default'} style={{ marginTop: 4 }}>
              {positionLabels[record.position]?.label || record.position}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Ordre',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.order - b.order,
      render: (order) => (
        <Tag color="orange" icon={<OrderedListOutlined />}>
          {order}
        </Tag>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggle(record)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      )
    },
    {
      title: 'Lien',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      width: 100,
      align: 'center',
      render: (linkUrl) => linkUrl ? (
        <Tooltip title={linkUrl}>
          <a href={linkUrl} target="_blank" rel="noopener noreferrer">
            <LinkOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
          </a>
        </Tooltip>
      ) : (
        <span style={{ color: '#bfbfbf' }}>-</span>
      )
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      render: (date) => (
        <Tooltip title={formatDate(date)}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
            {formatDate(date)}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #fff7e6;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #ffe7ba !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Bannières Website</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><PictureOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><EyeOutlined style={{ marginRight: 6 }} />Actives</span>}
                value={stats.active}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span>Hero</span>}
                value={stats.hero}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span>Milieu</span>}
                value={stats.middle}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8} md={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="Position"
                value={filterPosition}
                onChange={setFilterPosition}
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              >
                <Select.Option value="">Toutes</Select.Option>
                <Select.Option value="hero">Hero</Select.Option>
                <Select.Option value="middle">Milieu</Select.Option>
                <Select.Option value="bottom">Bas</Select.Option>
              </Select>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="Statut"
                value={filterStatus}
                onChange={setFilterStatus}
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              >
                <Select.Option value="">Tous</Select.Option>
                <Select.Option value="active">Actif</Select.Option>
                <Select.Option value="inactive">Inactif</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 8 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size={controlSize}
                onClick={handleAdd}
                style={{
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                  border: 'none'
                }}
              >
                Ajouter
              </Button>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${filteredBanners.length})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={filteredBanners.map((banner) => ({
              ...banner,
              key: banner._id,
            }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} bannières
                </span>
              ),
              showLessItems: isMobile
            }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 800 }}
            rowClassName={rowClassName}
          />
        </Card>
      </div>

      {/* Modal Ajout/Edition */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={40}
              style={{ backgroundColor: '#fa8c16' }}
              icon={isEditMode ? <EditOutlined /> : <PlusOutlined />}
            />
            <span>{isEditMode ? 'Modifier la bannière' : 'Nouvelle bannière'}</span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={isEditMode ? "Enregistrer" : "Créer"}
        cancelText="Annuler"
        centered
        width={isMobile ? 360 : 600}
        okButtonProps={{
          size: controlSize,
          style: {
            background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
            border: 'none'
          }
        }}
        cancelButtonProps={{ size: controlSize }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Titre"
                name="title"
                rules={[{ required: true, message: 'Le titre est requis' }]}
              >
                <Input placeholder="Ex: Téléchargez TIGOON Connect" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Sous-titre" name="subtitle">
                <Input placeholder="Ex: La meilleure app de gestion" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Position"
                name="position"
                rules={[{ required: true, message: 'La position est requise' }]}
                initialValue="middle"
              >
                <Select style={{ borderRadius: 8 }} onChange={(value) => setCurrentPosition(value)}>
                  <Select.Option value="hero">Hero (Haut de page)</Select.Option>
                  <Select.Option value="middle">Milieu de page</Select.Option>
                  <Select.Option value="bottom">Bas de page</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Ordre d'affichage" name="order" initialValue={0}>
                <Input type="number" placeholder="0" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Lien (URL)" name="linkUrl">
                <Input placeholder="https://..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Période de validité" name="dateRange">
                <RangePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%', borderRadius: 8 }}
                  placeholder={['Date début', 'Date fin']}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Actif"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Oui" unCheckedChildren="Non" />
              </Form.Item>
            </Col>

            {/* Section Encadré Promotionnel - uniquement pour position Hero */}
            {currentPosition === 'hero' && (
              <>
                <Col span={24}>
                  <Divider orientation="left" style={{ margin: '16px 0 8px' }}>
                    <Space>
                      <NotificationOutlined style={{ color: '#fa8c16' }} />
                      <span style={{ fontSize: 14, fontWeight: 500 }}>Encadré Promotionnel</span>
                    </Space>
                  </Divider>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Afficher l'encadré promo"
                    name="showPromoBox"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch checkedChildren="Oui" unCheckedChildren="Non" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Message promotionnel"
                    name="promoMessage"
                    tooltip="Le message principal de l'encadré (ex: 50% de réduction sur vos premières courses)"
                  >
                    <Input.TextArea
                      placeholder="Ex: 2150 FCFA d'économies sur votre première livraison + jusqu'à 15% de réduction"
                      rows={2}
                      style={{ borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Sous-texte"
                    name="promoSubtext"
                    tooltip="Texte secondaire (ex: Téléchargez l'application)"
                  >
                    <Input
                      placeholder="Ex: Téléchargez l'appli TIGOON Connect"
                      style={{ borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<Space><BgColorsOutlined /> Couleur de fond</Space>}
                    name="promoBackgroundColor"
                    initialValue="#D4A853"
                  >
                    <ColorPicker showText format="hex" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<Space><BgColorsOutlined /> Couleur du texte</Space>}
                    name="promoTextColor"
                    initialValue="#ffffff"
                  >
                    <ColorPicker showText format="hex" />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col span={24}>
              <Form.Item
                label="Image"
                name="image"
                rules={[{ required: !isEditMode, message: 'Une image est requise' }]}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  accept="image/*"
                >
                  {fileList.length < 1 && (
                    <div>
                      <PictureOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              {isEditMode && selectedBanner?.imageUrl && fileList.length === 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ marginBottom: 8, color: '#8c8c8c' }}>Image actuelle :</p>
                  <img
                    src={selectedBanner.imageUrl}
                    alt="Current"
                    style={{ width: 120, height: 70, objectFit: 'cover', borderRadius: 8 }}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Banners;
